import { useEffect, useRef } from 'react'
import { RefreshCw } from 'lucide-react'
import { Button } from '@highlight/ui'
import { useConnections } from '@renderer/hooks/use-connections'
import { useConnectProvider } from '@renderer/hooks/use-connect-provider'
import { ASHBY_API_KEY_PROVIDER_ID, useProviders } from '@renderer/hooks/use-providers'
import { integrationsAddApiKeyRoute } from '@shared/settings/settings-deeplink'
import WindowIpcRenderer from '@renderer/ipc/WindowIpc.renderer'
import type { ConnectionPrerequisite, NestedActionView, StagedAction } from './types'
import {
  getConnectionPrerequisiteCtaLabel,
  getConnectionPrerequisiteWarning,
  getPrerequisiteDisplayView,
  getUnsatisfiedConnectionPrerequisite,
  isRequestReconnectAction,
} from './prerequisites'

interface ConnectionPrerequisiteState<V> {
  unsatisfiedPrerequisite: ConnectionPrerequisite | null
  /** The action's view, swapped for its blocked-state view while unsatisfied. */
  displayView: V | undefined
  /** CTA label ("Reconnect Linear"); empty while the prerequisite is satisfied. */
  ctaLabel: string
  /**
   * Footer warning naming the block ("Connect Linear to continue"), to render in
   * place of the view's `invokeWarning`. Null when nothing is blocked, or when
   * the blocked card's own body already says it.
   */
  warning: string | null
  connectPrerequisite: () => void
}

/**
 * Connection-prerequisite state for a staged action cell: whether the action is
 * blocked on a provider connection, plus the view and CTA to render while it
 * is. For the dedicated request_reconnect action, calls `invoke` once the
 * connect flow it opened brings the connection back.
 */
export function useConnectionPrerequisite<V extends { nestedViews?: Array<NestedActionView> }>(
  action: Pick<StagedAction, 'toolSet' | 'toolName' | 'prerequisites'> & { view?: V },
  invoke?: () => void,
): ConnectionPrerequisiteState<V> {
  const { data: connections } = useConnections()
  const { data: providers } = useProviders()
  const connectProvider = useConnectProvider()
  const pendingReconnectInvokeRef = useRef(false)
  const unsatisfiedPrerequisite = getUnsatisfiedConnectionPrerequisite(action, connections)

  const connectPrerequisite = () => {
    if (!unsatisfiedPrerequisite) {
      return
    }

    // The Ashby API key has no OAuth flow to redirect to — it's added from the
    // Ashby card's menu in settings — so deep-link into Settings → Integrations
    // with the key dialog already open instead of a connect URL. The
    // satisfied/auto-invoke wiring below is unchanged: the key lands as an
    // `ashby` connection row, which clears the prerequisite through the same
    // connections query.
    if (unsatisfiedPrerequisite.provider === ASHBY_API_KEY_PROVIDER_ID) {
      void WindowIpcRenderer.openSettingsWindow(integrationsAddApiKeyRoute(ASHBY_API_KEY_PROVIDER_ID))
      pendingReconnectInvokeRef.current = isRequestReconnectAction(action)
      return
    }

    void connectProvider(unsatisfiedPrerequisite.provider, {
      connectionId: unsatisfiedPrerequisite.connectionId,
    }).then((opened) => {
      pendingReconnectInvokeRef.current = opened && isRequestReconnectAction(action)
    })
  }

  useEffect(() => {
    if (!pendingReconnectInvokeRef.current || unsatisfiedPrerequisite || !isRequestReconnectAction(action)) {
      return
    }
    pendingReconnectInvokeRef.current = false
    invoke?.()
  }, [action, invoke, unsatisfiedPrerequisite])

  return {
    unsatisfiedPrerequisite,
    displayView: unsatisfiedPrerequisite ? getPrerequisiteDisplayView(action) : action.view,
    ctaLabel: unsatisfiedPrerequisite
      ? getConnectionPrerequisiteCtaLabel(action, unsatisfiedPrerequisite, connections, providers)
      : '',
    warning: unsatisfiedPrerequisite
      ? getConnectionPrerequisiteWarning(action, unsatisfiedPrerequisite, connections, providers)
      : null,
    connectPrerequisite,
  }
}

interface PrerequisiteCtaButtonProps {
  label: string
  isFocused?: boolean
  onClick: () => void
  className?: string
}

/** Bold "Connect/Reconnect <provider>" CTA rendered in place of an action's invoke button. */
export function PrerequisiteCtaButton({ label, isFocused, onClick, className }: PrerequisiteCtaButtonProps) {
  return (
    <Button
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      variant="primary"
      left={<RefreshCw />}
      hotkey={isFocused ? 'cmd + enter' : undefined}
      className={className}
    >
      {label}
    </Button>
  )
}
